import {tokenizeQuery} from '@github-ui/filter-query'
import {useLocation} from '@github-ui/react-router'
import {isStaff} from '@github-ui/stats'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useEffect, useRef} from 'react'

import {useQueryBulk} from '../contexts/QueryContext'

type SearchMetricState = {
  query: string
  timestamp: number
  searchId: string
  issueSearchType?: string | null
  eventSent?: boolean
}

// Helper to build common event payload
const buildEventPayload = (state: SearchMetricState) => {
  return {
    query: state.query,
    search_id: state.searchId,
    is_staff: isStaff(),
    issue_search_type: state.issueSearchType ?? 'UNKNOWN',
  }
}

// Check if a query is an issue search with free-text
const isIssueSearchWithFreeText = (query: string): boolean => {
  if (!query || query.trim() === '') return false

  // Use tokenizeQuery to properly parse the query into segments
  const segments = tokenizeQuery(query)

  // Only track searches that explicitly include "is:issue"
  const hasIsIssue = segments.some(
    segment =>
      segment.type === 'filter' &&
      segment.key.toLowerCase() === 'is' &&
      segment.values.some(value => value.toLowerCase() === 'issue'),
  )
  if (!hasIsIssue) return false

  // Check if there are any text segments (non-filter segments)
  return segments.some(segment => segment.type === 'text' && segment.value.trim().length > 0)
}

/**
 * Hook to track search success metrics for issue searches.
 * Only tracks searches that include "is:issue" and have free-text content.
 *
 * Each qualifying search is assigned a unique ID that's included in both:
 * - The initial search event (search.performed_with_freetext)
 * - Any subsequent success events (search.successful_search_action)
 *
 * This allows linking searches to outcomes in analytics for correlation analysis.
 */
export function useSearchMetrics() {
  const {bulkJobId} = useQueryBulk()
  const {sendAnalyticsEvent} = useAnalytics()
  const {search: locationSearch} = useLocation()

  // Track the last search that was explicitly submitted with free-text
  const lastSearchRef = useRef<SearchMetricState | null>(null)

  // Track previous URL query to detect changes
  const prevUrlQueryRef = useRef<string | null>(null)

  const trackSearchSubmit = useCallback(
    (query: string) => {
      const isTrackable = isIssueSearchWithFreeText(query)

      // If this is not a trackable search, clear any pending search and return
      if (!isTrackable) {
        lastSearchRef.current = null
        return
      }

      // If this is the same query as the last one, don't track it again
      if (lastSearchRef.current && lastSearchRef.current.query === query) {
        return
      }

      // Generate a unique ID for this search
      const searchId = crypto.randomUUID()

      // Store search state but don't send event yet - wait for issueSearchType to be set
      lastSearchRef.current = {
        query,
        timestamp: Date.now(),
        searchId,
        eventSent: false,
      }
    },
    [lastSearchRef],
  )

  // Track bulk action as success
  useEffect(() => {
    if (bulkJobId && lastSearchRef.current) {
      const timeSinceSearch = Date.now() - lastSearchRef.current.timestamp

      sendAnalyticsEvent('search.successful_search_action', 'SEARCH_METRICS', {
        ...buildEventPayload(lastSearchRef.current),
        action: 'bulk_action',
        time_to_action_ms: timeSinceSearch,
      })

      // Clear the tracked search after success
      lastSearchRef.current = null
    }
  }, [bulkJobId, sendAnalyticsEvent])

  // Function to manually track issue navigation success
  const trackIssueNavigation = useCallback(
    (position?: number) => {
      if (lastSearchRef.current) {
        const timeSinceSearch = Date.now() - lastSearchRef.current.timestamp

        sendAnalyticsEvent('search.successful_search_action', 'SEARCH_METRICS', {
          ...buildEventPayload(lastSearchRef.current),
          action: 'issue_navigation',
          time_to_action_ms: timeSinceSearch,
          ...(position !== undefined && {position: position + 1}),
        })

        // Clear the tracked search after success
        lastSearchRef.current = null
      }
    },
    [sendAnalyticsEvent],
  )

  // Track search when URL query changes (e.g., from picker selection)
  useEffect(() => {
    const urlQuery = new URLSearchParams(locationSearch).get('q')

    // Skip initial load - only track when query actually changes
    if (prevUrlQueryRef.current === null) {
      prevUrlQueryRef.current = urlQuery
      return
    }

    // Only track if query changed
    if (urlQuery && urlQuery !== prevUrlQueryRef.current) {
      trackSearchSubmit(urlQuery)
    }
    prevUrlQueryRef.current = urlQuery
  }, [locationSearch, trackSearchSubmit])

  // Function to update the issue search type for the current search
  const updateIssueSearchType = useCallback(
    (issueSearchType: string | null | undefined) => {
      if (lastSearchRef.current && issueSearchType !== undefined) {
        lastSearchRef.current.issueSearchType = issueSearchType

        // Send the search event now that we have the issueSearchType
        if (!lastSearchRef.current.eventSent) {
          lastSearchRef.current.eventSent = true
          sendAnalyticsEvent(
            'search.performed_with_freetext',
            'SEARCH_METRICS',
            buildEventPayload(lastSearchRef.current),
          )
        }
      }
    },
    [sendAnalyticsEvent],
  )

  return {
    trackIssueNavigation,
    updateIssueSearchType,
    // Exposed for testing - tracking is primarily done via URL changes
    trackSearchSubmit,
  }
}
